import React, { useCallback, useState, useRef, useEffect } from 'react';
import { colors } from '../../styles/theme';
import { TIME_ESTIMATE, MESSAGES } from '../../utils/constants';

interface TimeEstimatePickerProps {
  id: string;
  timeEstimate: number | undefined;
  canHaveEstimate: boolean;
  subtreeEstimate?: number;
  onTimeEstimateChange: (hours: number | undefined) => void;
  onDropdownOpenChange?: (isOpen: boolean) => void;
  /** When false, render nothing if there's no estimate to show (keeps the
   *  compact row uncluttered; the empty "set estimate" affordance only appears
   *  when the node is expanded). Defaults to true. */
  showWhenEmpty?: boolean;
}

const TimeEstimatePicker: React.FC<TimeEstimatePickerProps> = ({
  id,
  timeEstimate,
  canHaveEstimate,
  subtreeEstimate,
  onTimeEstimateChange,
  onDropdownOpenChange,
  showWhenEmpty = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleBadgeClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canHaveEstimate) {
      setShowDropdown(prev => {
        const newValue = !prev;
        onDropdownOpenChange?.(newValue);
        return newValue;
      });
    }
  }, [canHaveEstimate, onDropdownOpenChange]);

  const handlePresetSelect = useCallback((hours: number) => {
    onTimeEstimateChange(hours);
    setShowDropdown(false);
    setShowCustomInput(false);
    onDropdownOpenChange?.(false);
  }, [onTimeEstimateChange, onDropdownOpenChange]);

  const handleOtherSelect = useCallback(() => {
    setShowCustomInput(true);
    setCustomValue('');
    // Focus input after a small delay to ensure it's rendered
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleCustomSubmit = useCallback(() => {
    const value = parseFloat(customValue);
    if (!isNaN(value) && value >= 0) {
      // Round to 1 decimal place
      const rounded = Math.round(value * 10) / 10;
      onTimeEstimateChange(rounded);
      setShowDropdown(false);
      setShowCustomInput(false);
      onDropdownOpenChange?.(false);
    }
  }, [customValue, onTimeEstimateChange, onDropdownOpenChange]);

  const handleCustomKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleCustomSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setShowCustomInput(false);
      setCustomValue('');
    }
  }, [handleCustomSubmit]);

  const handleRemoveEstimate = useCallback(() => {
    onTimeEstimateChange(undefined);
    setShowDropdown(false);
    setShowCustomInput(false);
    onDropdownOpenChange?.(false);
  }, [onTimeEstimateChange, onDropdownOpenChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setShowCustomInput(false);
        onDropdownOpenChange?.(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown, onDropdownOpenChange]);

  // Format display value
  const getDisplayValue = (): string | null => {
    if (timeEstimate !== undefined) {
      // Node has its own estimate (leaf or parent with all children done)
      return `${timeEstimate}h`;
    } else if (!canHaveEstimate && subtreeEstimate !== undefined && subtreeEstimate > 0) {
      // Parent node without all children done: show sum with "+"
      return `${subtreeEstimate}h+`;
    }
    return null; // No display value
  };

  const displayValue = getDisplayValue();
  const showBadge = displayValue !== null || (canHaveEstimate && showWhenEmpty);

  if (!showBadge) {
    return null;
  }

  const tooltipText = canHaveEstimate
    ? MESSAGES.TIME_ESTIMATE_TOOLTIP
    : MESSAGES.TIME_ESTIMATE_SUM_TOOLTIP;

  return (
    <div className="time-estimate-picker-container" ref={dropdownRef}>
      <button
        className="time-estimate-badge"
        style={{
          backgroundColor: displayValue ? TIME_ESTIMATE.COLOR : 'transparent',
          color: displayValue ? colors.text.inverted : 'var(--text-secondary)',
          cursor: canHaveEstimate ? 'pointer' : 'default',
          opacity: !canHaveEstimate ? 0.8 : 1,
          border: displayValue ? 'none' : '1px dashed var(--text-secondary)',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '11px',
          fontWeight: displayValue ? 'bold' : 'normal',
        }}
        title={tooltipText}
        onClick={handleBadgeClick}
        disabled={!canHaveEstimate}
      >
        {displayValue || '⏱️'}
      </button>

      {showDropdown && canHaveEstimate && (
        <div className="time-estimate-dropdown">
          {!showCustomInput ? (
            <>
              {TIME_ESTIMATE.PRESETS.map((hours, index) => (
                <button
                  key={hours}
                  className="time-estimate-dropdown-item"
                  style={{
                    backgroundColor: TIME_ESTIMATE.COLOR,
                    color: colors.text.inverted,
                  }}
                  onClick={() => handlePresetSelect(hours)}
                >
                  {TIME_ESTIMATE.LABELS[index]}
                </button>
              ))}
              <button
                className="time-estimate-dropdown-item"
                style={{
                  backgroundColor: TIME_ESTIMATE.COLOR,
                  color: colors.text.inverted,
                }}
                onClick={handleOtherSelect}
              >
                Other
              </button>
              {timeEstimate !== undefined && (
                <button
                  className="time-estimate-dropdown-item"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    borderTop: '1px solid var(--border-primary)',
                  }}
                  onClick={handleRemoveEstimate}
                >
                  Remove
                </button>
              )}
            </>
          ) : (
            <div
              className="time-estimate-custom-input"
              style={{
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <input
                ref={inputRef}
                type="number"
                min="0"
                step="0.1"
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                onKeyDown={handleCustomKeyDown}
                placeholder="Hours"
                style={{
                  padding: '4px 6px',
                  borderRadius: '3px',
                  border: '1px solid var(--border-primary)',
                  backgroundColor: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: '12px',
                  width: '100%',
                }}
              />
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={handleCustomSubmit}
                  style={{
                    flex: 1,
                    padding: '4px',
                    borderRadius: '3px',
                    border: 'none',
                    backgroundColor: TIME_ESTIMATE.COLOR,
                    color: colors.text.inverted,
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Set
                </button>
                <button
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomValue('');
                  }}
                  style={{
                    flex: 1,
                    padding: '4px',
                    borderRadius: '3px',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeEstimatePicker;
